import React from "react";
import { AuditLogRuleCondition } from "@common/types/auditLog";
import { currencyFormatter } from "@common/helpers/currency";
import { isRuleConditionAmountRounding } from "@common/helpers/rules";
import { isNumber, isObject } from "@common/helpers/typeGuards";
import {
    ROUNDING_LEVEL_LABELS,
    RuleConditionAmount,
} from "@common/types/transactionRule";
import styles from "../HistoryRuleDetails.module.scss";

function renderAmountRoundingCondition(value: unknown): React.ReactNode {
    if (!isRuleConditionAmountRounding(value)) {
        return null;
    }
    const { level, isCredit } = value;
    const direction = isCredit ? "Cash in" : "Cash out";
    const label = ROUNDING_LEVEL_LABELS[level];
    return (
        <>
            {direction} <span className={styles.bold}>{label}</span>
        </>
    );
}

function renderAmountRoundingDirection(value: unknown): React.ReactNode {
    if (!isRuleConditionAmountRounding(value)) {
        return null;
    }
    const { isCredit } = value;
    return <>{isCredit ? "Cash in" : "Cash out"}</>;
}

function renderValues(values: unknown[]): React.ReactNode {
    const strings = values.filter((v): v is string => typeof v === "string");
    if (strings.length === 0) {
        return null;
    }
    return <span className={styles.bold}>{strings.join(", ")}</span>;
}

function renderAmountCondition(value: unknown): React.ReactNode {
    if (!isObject(value)) {
        return null;
    }
    const amount = value as RuleConditionAmount;
    const hasMin = isNumber(amount.min);
    const hasMax = isNumber(amount.max);
    if (!hasMin && !hasMax) {
        return null;
    }

    const isCredit =
        (hasMin && amount.min! >= 0) || (hasMax && amount.max! > 0);
    const type = isCredit ? "Cash in" : "Cash out";

    if (hasMin && hasMax) {
        if (amount.min === amount.max) {
            return (
                <>
                    {type} is{" "}
                    <span className={styles.bold}>
                        {currencyFormatter.format(Math.abs(amount.min!))}
                    </span>
                </>
            );
        }
        const lower = isCredit ? amount.min! : amount.max!;
        const upper = isCredit ? amount.max! : amount.min!;
        return (
            <>
                {type} between{" "}
                <span className={styles.bold}>
                    {currencyFormatter.format(Math.abs(lower))}
                </span>{" "}
                and{" "}
                <span className={styles.bold}>
                    {currencyFormatter.format(Math.abs(upper))}
                </span>
            </>
        );
    }

    if (hasMin) {
        return (
            <>
                {type}{" "}
                {amount.min !== 0 && (
                    <>
                        {isCredit ? "above" : "below"}{" "}
                        <span className={styles.bold}>
                            {currencyFormatter.format(Math.abs(amount.min!))}
                        </span>
                    </>
                )}
            </>
        );
    }

    return (
        <>
            {type}{" "}
            {amount.max !== 0 && (
                <>
                    {isCredit ? "below" : "above"}{" "}
                    <span className={styles.bold}>
                        {currencyFormatter.format(Math.abs(amount.max!))}
                    </span>
                </>
            )}
        </>
    );
}

export function renderCondition(
    condition: AuditLogRuleCondition,
): React.ReactNode {
    const values = Array.isArray(condition.value)
        ? condition.value
        : [condition.value];

    switch (condition.conditionType) {
        case "entity":
            return <>From {renderValues(values)}</>;
        case "category":
        case "suggested_category":
            return <>Category is {renderValues(values)}</>;
        case "counterparty":
            return <>Counterparty is {renderValues(values)}</>;
        case "description_is":
            return <>Bank description is {renderValues(values)}</>;
        case "description_includes":
            return <>Bank description contains {renderValues(values)}</>;
        case "description_does_not_include":
            return (
                <>Bank description does not contain {renderValues(values)}</>
            );
        case "amount":
            return renderAmountCondition(condition.value);
        case "financial_accounts":
            return <>Account is {renderValues(values)}</>;
        case "subaccounts":
            return <>Subaccount is {renderValues(values)}</>;
        case "class":
            return <>Class is {renderValues(values)}</>;
        case "similar_to":
            return <>Similar to {renderValues(values)}</>;
        case "amount_is_round":
            return (
                <>
                    Amount is round{" "}
                    {renderAmountRoundingCondition(condition.value)}
                </>
            );
        case "amount_is_not_round":
            return (
                <>
                    Amount is not round{" "}
                    {renderAmountRoundingDirection(condition.value)}
                </>
            );
        default:
            return null;
    }
}
